import Company, { CompaniesRegistrar } from '../models/company.js';

export type VerificationState = 'verified' | 'invalid' | 'warning';

export interface VerificationFactor {
  key: 'businessRegistry' | 'taxAuthority' | 'bankValidation';
  label: string;
  state: VerificationState;
  issue?: string;
}

interface FactorDefinition {
  key: VerificationFactor['key'];
  label: string;
  registrarType: string;
}

const FACTOR_DEFINITIONS: FactorDefinition[] = [
  { key: 'businessRegistry', label: 'Companies House', registrarType: 'REGISTRATION' },
  { key: 'taxAuthority', label: 'HMRC', registrarType: 'TAXATION' },
  { key: 'bankValidation', label: 'Open Banking', registrarType: 'BANK_VALIDATION' },
];

/** Among entries sharing a registrarType, prefer the last validated one; arrays are append-ordered
 * so "last" approximates "most recent" without a timestamp field to properly tie-break on. */
function findRegistrarEntry(registrars: CompaniesRegistrar[], registrarType: string): CompaniesRegistrar | undefined {
  const matches = registrars.filter((registrar) => registrar.registrarType === registrarType);
  if (matches.length < 1) return undefined;

  const validatedMatches = matches.filter((registrar) => registrar.validated);
  return validatedMatches.length > 0 ? validatedMatches[validatedMatches.length - 1] : matches[matches.length - 1];
}

/** Unverified (no registrar entry yet, or one present but not validated) defaults to `invalid` -
 * `warning` is reserved for "verified successfully but something's flagged", which has no data
 * source yet (see the deferred backend-issue-field work) and so isn't produced here today. */
function deriveFactor(definition: FactorDefinition, registrars: CompaniesRegistrar[]): VerificationFactor {
  const { key, label, registrarType } = definition;
  const entry = findRegistrarEntry(registrars, registrarType);
  return { key, label, state: entry?.validated ? 'verified' : 'invalid' };
}

export function deriveVerificationFactors(company: Company): VerificationFactor[] {
  const registrars = company.companiesRegistrar ?? [];
  return FACTOR_DEFINITIONS.map((definition) => deriveFactor(definition, registrars));
}

export function aggregateVerificationState(factors: VerificationFactor[]): VerificationState {
  if (factors.some((factor) => factor.state === 'invalid')) return 'invalid';
  if (factors.some((factor) => factor.state === 'warning')) return 'warning';
  return 'verified';
}
